import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material';
import { toast } from 'react-toastify';
import supplierService, {
  Supplier,
  SupplierInput,
  SupplierType,
  SUPPLIER_TYPE_LABELS,
} from '../../services/supplierService';

interface Props {
  open: boolean;
  supplier: Supplier | null;
  onClose: () => void;
  onSaved: (s: Supplier) => void;
}

const EMPTY: SupplierInput = {
  name: '',
  type: 'distributor',
  phone: '',
  email: '',
  address: '',
  nationalId: '',
  economicCode: '',
  website: '',
  notes: '',
  isActive: true,
};

const SupplierDialog: React.FC<Props> = ({ open, supplier, onClose, onSaved }) => {
  const [form, setForm] = useState<SupplierInput>(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (supplier) {
      setForm({
        name: supplier.name || '',
        type: supplier.type || 'distributor',
        phone: supplier.phone || '',
        email: supplier.email || '',
        address: supplier.address || '',
        nationalId: supplier.nationalId || '',
        economicCode: supplier.economicCode || '',
        website: supplier.website || '',
        notes: supplier.notes || '',
        isActive: supplier.isActive !== false,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, supplier]);

  const set = (k: keyof SupplierInput) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('نام تأمین‌کننده الزامی است');
      return;
    }
    setSaving(true);
    try {
      const body: SupplierInput = { ...form, name: form.name.trim() };
      const saved = supplier
        ? await supplierService.updateSupplier(supplier._id, body)
        : await supplierService.createSupplier(body);
      toast.success(supplier ? 'تأمین‌کننده ویرایش شد' : 'تأمین‌کننده اضافه شد');
      onSaved(saved);
    } catch (e: any) {
      toast.error(e?.message || 'ذخیره ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" dir="rtl">
      <DialogTitle>{supplier ? 'ویرایش تأمین‌کننده' : 'افزودن تأمین‌کننده'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField label="نام شرکت" value={form.name} onChange={set('name')} fullWidth required autoFocus />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="نوع"
              value={form.type || 'distributor'}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as SupplierType }))}
              fullWidth
            >
              {(Object.keys(SUPPLIER_TYPE_LABELS) as SupplierType[]).map((t) => (
                <MenuItem key={t} value={t}>
                  {SUPPLIER_TYPE_LABELS[t]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="تلفن" value={form.phone} onChange={set('phone')} fullWidth inputProps={{ dir: 'ltr' }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="ایمیل" value={form.email} onChange={set('email')} fullWidth inputProps={{ dir: 'ltr' }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="وب‌سایت" value={form.website} onChange={set('website')} fullWidth inputProps={{ dir: 'ltr' }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="شناسه ملی" value={form.nationalId} onChange={set('nationalId')} fullWidth inputProps={{ dir: 'ltr' }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="کد اقتصادی" value={form.economicCode} onChange={set('economicCode')} fullWidth inputProps={{ dir: 'ltr' }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="نشانی" value={form.address} onChange={set('address')} fullWidth multiline minRows={2} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="یادداشت داخلی" value={form.notes} onChange={set('notes')} fullWidth multiline minRows={2} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive !== false}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                />
              }
              label="فعال"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          انصراف
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'در حال ذخیره…' : 'ذخیره'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupplierDialog;
